import { toast } from "react-toastify";
import moment from "moment";
import { saveAs } from "file-saver";
import API from "../../Api/Api";
import NEW_API from "../../Api/NewApi";
import { IMAGE_END_POINT } from "../../Api/EndPoints";
import { scopeParams } from "./appCustomers";

// ---------------------------------------------------------------------------
// Partner-side projects (deck "Onglet Prospect - Projet"): custom flat routes
// /api/projects-* (scoped server-side); tunnel actions go through the core
// /api/actions CRUD with a `project` relation, like the prospect tunnel.
// ---------------------------------------------------------------------------

export const fetchProjects =
  (page = 1, search, filters, signal) =>
  async (dispatch, getState) => {
    try {
      if (page === 1) dispatch({ type: "FETCH_PROJECTS_LOADING" });
      const { data } = await NEW_API.get("/api/projects-list", {
        params: {
          page,
          search: search || undefined,
          status: filters?.status?.code || undefined,
          toConfirm: filters?.toConfirm ? 1 : undefined,
          salesRep: filters?.salesRep?.code || undefined,
          model: filters?.model?.code || undefined,
          source: filters?.source || undefined,
          relanceDate: filters?.relanceDate
            ? moment(filters.relanceDate).format("YYYY-MM-DD")
            : undefined,
          ...scopeParams(getState),
        },
        signal,
      });
      dispatch({
        type:
          page === 1
            ? "FETCH_PROJECTS_SUCCESS"
            : "FETCH_PROJECTS_LOAD_MORE_SUCCESS",
        payload: data,
      });
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
        return;
      console.log(error);
      dispatch({ type: "FETCH_PROJECTS_ERROR", payload: error?.message });
    }
  };

export const fetchProjectStats = (signal) => async (dispatch, getState) => {
  try {
    const { data } = await NEW_API.get("/api/projects-stats", {
      params: scopeParams(getState),
      signal,
    });
    dispatch({ type: "FETCH_PROJECT_STATS_SUCCESS", payload: data?.data });
  } catch (error) {
    if (error.name === "CanceledError" || error.code === "ERR_CANCELED") return;
    console.log(error);
  }
};

export const fetchProject = (id, signal) => async (dispatch) => {
  try {
    dispatch({ type: "FETCH_CURRENT_PROJECT_LOADING" });
    const { data } = await NEW_API.get(`/api/projects-one/${id}`, { signal });
    dispatch({ type: "FETCH_CURRENT_PROJECT_SUCCESS", payload: data?.data });
  } catch (error) {
    if (error.name === "CanceledError" || error.code === "ERR_CANCELED") return;
    console.log(error);
    dispatch({ type: "FETCH_CURRENT_PROJECT_ERROR" });
  }
};

export const createProject = (payload, t, callback) => async (dispatch) => {
  try {
    dispatch({ type: "CREATE_PROJECT_LOADING" });
    const { data } = await NEW_API.post("/api/projects-create", {
      data: payload,
    });
    dispatch({ type: "CREATE_PROJECT_SUCCESS", payload: data?.data });
    toast.success(t("Project successfully created!"));
    if (callback) callback(data?.data);
  } catch (error) {
    console.log(error);
    dispatch({ type: "CREATE_PROJECT_ERROR" });
    toast.error(t("An error has occurred!"));
  }
};

export const updateProject = (id, payload, t, callback) => async (dispatch) => {
  try {
    dispatch({ type: "UPDATE_PROJECT_LOADING" });
    const { data } = await NEW_API.put(`/api/projects-update/${id}`, {
      data: payload,
    });
    dispatch({ type: "UPDATE_PROJECT_SUCCESS", payload: data?.data });
    if (t) toast.success(t("Project successfully updated!"));
    if (callback) callback(data?.data);
  } catch (error) {
    console.log(error);
    dispatch({ type: "UPDATE_PROJECT_ERROR" });
    if (t) toast.error(t("An error has occurred!"));
  }
};

// Scénario B quote errors (backend codes) → raw i18n keys.
const QUOTE_ERRORS = {
  QUOTE_LOCKED: "This quote has an order: it can no longer be edited or deleted",
  SALES_REP_NOT_FOUND: "This commercial is not one of the project's store",
  QUOTE_ALREADY_ACCEPTED: "This quote has already been accepted",
  QUOTE_NOT_ACCEPTED_BY_CUSTOMER: "This quote is no longer waiting for an order",
  QUOTE_TRANSITION_REFUSED: "This quote is no longer waiting for an order",
  NO_RECIPIENT: "The recipient must be an app customer followed by this store",
};
const toastQuoteError = (error, t) => {
  const body = error?.response?.data?.error;
  toast.error(
    t(QUOTE_ERRORS[body?.details?.code ?? body?.message] ?? "An error has occurred!")
  );
};

/** Devis tab: save (create/update) a quote built from SOFT line items. */
export const saveProjectQuote =
  (projectId, quote, t, callback) => async (dispatch) => {
    try {
      dispatch({ type: "UPDATE_PROJECT_LOADING" });
      const { data } = await NEW_API.post(`/api/projects-quotes/${projectId}`, {
        data: quote,
      });
      dispatch({ type: "UPDATE_PROJECT_SUCCESS", payload: data?.data });
      // "Send the quote": saved, but the customer was not told.
      const sendError = data?.meta?.sendError;
      if (sendError) {
        toast.warning(
          `${t("Quote saved but not sent")} — ${t(
            QUOTE_ERRORS[sendError] ?? "An error has occurred!"
          )}`
        );
      } else {
        toast.success(t("Quote successfully saved!"));
      }
      if (callback) callback();
    } catch (error) {
      console.log(error);
      dispatch({ type: "UPDATE_PROJECT_ERROR" });
      toastQuoteError(error, t);
    }
  };

// ---------------------------------------------------------------------------
// Scénario B — the advisor confirms the order. Every mutation refetches the
// project (Devis tab) and bumps `version` (list status + "à confirmer" tile).
// ---------------------------------------------------------------------------

const refreshProject = (projectId) => (dispatch) => {
  dispatch(fetchProject(projectId));
  dispatch({ type: "PROJECTS_TOUCHED" });
};

/** B4 / C2 dialog data (stock per line, buyer, deposit). Null on error. */
export const fetchQuoteOrderPreview =
  (projectId, quoteId, signal) => async () => {
    try {
      const { data } = await NEW_API.get(
        `/api/projects-quotes-order/${projectId}/${quoteId}`,
        { signal }
      );
      return data?.data ?? null;
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
        return null;
      console.log(error);
      return null;
    }
  };

/**
 * B4 "Passer la commande": the store's cart (order funnel) + the sale to the
 * customer. Resolves { order: { cartId, cartNumber, appOrderId } } on success,
 * { unavailable: lines } when a unit was sold meanwhile (the quote is now
 * "Indisponible"), {} else.
 */
export const createQuoteOrder =
  (projectId, quoteId, body, t) => async (dispatch) => {
    try {
      const { data } = await NEW_API.post(
        `/api/projects-quotes-order/${projectId}/${quoteId}`,
        { data: body }
      );
      toast.success(
        t("Order no. {{number}} created — complete it in the order funnel", {
          number: data?.data?.cartNumber,
        })
      );
      dispatch(refreshProject(projectId));
      return { order: data?.data };
    } catch (error) {
      const lines = error?.response?.data?.error?.details?.lines;
      if (error?.response?.status === 422 && lines) {
        dispatch(refreshProject(projectId));
        return { unavailable: lines };
      }
      console.log(error);
      toastQuoteError(error, t);
      return {};
    }
  };

// ---------------------------------------------------------------------------
// Client sheet — the store's sale to its customer (/api/customer-sales): the
// customer's payments to the store, the store's delivery, the cancellation.
// Never Poseidon orders, payments or invoices. Each action resolves the
// refreshed sale (null on error, after a toast).
// ---------------------------------------------------------------------------

const SALE_ERRORS = {
  PAYMENT_EXCEEDS_TOTAL: "The payments can't exceed the order total",
  INVALID_AMOUNT: "Enter an amount above 0",
  SALE_CANCELLED: "This order is cancelled",
  SALE_ALREADY_CANCELLED: "This order is cancelled",
  ORDER_CANCELLED: "This order is cancelled",
  SALE_DELIVERED: "This order is already delivered",
  ORDER_DELIVERED: "This order is already delivered",
  NO_DATE: "No delivery date yet",
  INVALID_DATE: "Pick a date from today on",
};

/** The sale of the client sheet; null on error. */
export const fetchSale = (saleId, signal) => async () => {
  try {
    const { data } = await NEW_API.get(`/api/customer-sales/${saleId}`, {
      signal,
    });
    return data?.data ?? null;
  } catch (error) {
    if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
      return null;
    console.log(error);
    return null;
  }
};

/** One sale action, then the project is refetched (quotes, stepper, Discussion). */
const saleAction =
  (method, path, body, t, success, projectId) => async (dispatch) => {
    try {
      const { data } = await NEW_API.request({
        method,
        url: `/api/customer-sales/${path}`,
        data: body ? { data: body } : undefined,
      });
      toast.success(t(success));
      const project = projectId ?? data?.data?.project?.id;
      if (project) dispatch(refreshProject(project));
      return data?.data ?? null;
    } catch (error) {
      console.log(error);
      const failure = error?.response?.data?.error;
      toast.error(
        t(SALE_ERRORS[failure?.details?.code ?? failure?.message] ?? "An error has occurred!")
      );
      return null;
    }
  };

export const addSalePayment = (saleId, payment, t) =>
  saleAction("post", `${saleId}/payments`, payment, t, "Payment recorded");
export const updateSalePayment = (saleId, paymentId, payment, t) =>
  saleAction("put", `${saleId}/payments/${paymentId}`, payment, t, "Payment updated");
export const deleteSalePayment = (saleId, paymentId, t) =>
  saleAction("delete", `${saleId}/payments/${paymentId}`, null, t, "Payment deleted");
export const validateSaleDelivery = (saleId, t) =>
  saleAction("post", `${saleId}/delivery/validate`, {}, t, "Delivery date has been validated");
export const proposeSaleDelivery = (saleId, proposal, t) =>
  saleAction("post", `${saleId}/delivery/propose`, proposal, t, "New date proposed to the customer");
export const markSaleDelivered = (saleId, projectId, t) =>
  saleAction("post", `${saleId}/delivery/delivered`, {}, t, "Order marked as delivered", projectId);
export const cancelSale = (saleId, projectId, body, t) =>
  saleAction("post", `${saleId}/cancel`, body, t, "Order cancelled", projectId);

/** "Annuler la commande" dialog: what cancelling will do. Null on error. */
export const fetchSaleCancelPreview = (saleId, signal) => async () => {
  try {
    const { data } = await NEW_API.get(`/api/customer-sales/${saleId}/cancel`, {
      signal,
    });
    return data?.data ?? null;
  } catch (error) {
    if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
      return null;
    console.log(error);
    return null;
  }
};

/** Order funnel "Projet client" (D1): the ongoing projects. */
export const fetchOngoingProjects =
  (signal) => async (dispatch, getState) => {
    try {
      const { data } = await NEW_API.get("/api/projects-list", {
        params: { page: 1, pageSize: 200, ongoing: 1, ...scopeParams(getState) },
        signal,
      });
      return data?.data ?? [];
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
        return [];
      console.log(error);
      return [];
    }
  };

/** A contact as the Espace clients sheet (AppCustomerModal) expects it. */
export const fetchCustomerRow = (customerId) => async () => {
  try {
    const { data } = await NEW_API.get(`/api/customers/${customerId}`);
    return data?.data ?? null;
  } catch (error) {
    console.log(error);
    return null;
  }
};

/** ⋮ "Marquer comme accepté hors app" (note: Téléphone / Signé en magasin / Autre). */
export const acceptQuoteForCustomer =
  (projectId, quoteId, note, t, callback) => async (dispatch) => {
    try {
      await NEW_API.post(`/api/projects-quotes-accept/${projectId}/${quoteId}`, {
        data: { note },
      });
      toast.success(t("Quote marked as accepted"));
      dispatch(refreshProject(projectId));
      if (callback) callback();
    } catch (error) {
      console.log(error);
      toastQuoteError(error, t);
    }
  };

/** C7 "Dupliquer le devis". */
export const duplicateProjectQuote =
  (projectId, quoteId, t) => async (dispatch) => {
    try {
      await NEW_API.post(
        `/api/projects-quotes-duplicate/${projectId}/${quoteId}`,
        { data: {} }
      );
      toast.success(t("Quote duplicated"));
      dispatch(refreshProject(projectId));
    } catch (error) {
      console.log(error);
      toastQuoteError(error, t);
    }
  };

export const deleteProjectQuote =
  (projectId, quoteId, t) => async (dispatch) => {
    try {
      await NEW_API.delete(`/api/projects-quotes/${projectId}/${quoteId}`);
      dispatch(fetchProject(projectId));
      toast.success(t("Quote successfully deleted!"));
    } catch (error) {
      console.log(error);
      toastQuoteError(error, t);
    }
  };

/** Generates (or regenerates) the quote PDF, then downloads it. */
export const downloadQuotePdf = (quoteId, t) => async () => {
  try {
    const { data } = await NEW_API.get(`/api/quotes/${quoteId}/pdf`);
    if (data?.url) {
      saveAs(`${IMAGE_END_POINT}${data.url}`, `devis-${quoteId}.pdf`);
    }
  } catch (error) {
    console.log(error);
    toast.error(t("An error has occurred!"));
  }
};

/** "Voir le devis": generates the PDF and opens it in a new tab. */
export const viewQuotePdf = (quoteId, t) => async () => {
  try {
    const { data } = await NEW_API.get(`/api/quotes/${quoteId}/pdf`);
    if (data?.url) window.open(`${IMAGE_END_POINT}${data.url}`, "_blank");
  } catch (error) {
    console.log(error);
    toast.error(t("An error has occurred!"));
  }
};

// "Envoyer le projet par e-mail": the "Nouveau projet" e-mail (button + QR
// code opening the project in the app) to the project's main contact.
const PROJECT_EMAIL_ERRORS = {
  PROJECT_EMAIL_TEMPLATE_NOT_CONFIGURED: "The project e-mail template is not configured",
  NO_MAIN_CONTACT: "This project has no main contact",
  NO_EMAIL: "This customer has no e-mail address",
  SEND_FAILED: "The e-mail could not be sent",
};
export const sendProjectEmail = (projectId, t) => async () => {
  try {
    await NEW_API.post(`/api/projects-send-email/${projectId}`, { data: {} });
    toast.success(t("Project e-mail sent!"));
  } catch (error) {
    console.log(error);
    const code = error?.response?.data?.error?.message;
    toast.error(t(PROJECT_EMAIL_ERRORS[code] ?? "An error has occurred!"));
  }
};

/** "Envoyer le devis": pushes it into the prospect's app message feed. */
export const sendProjectQuote =
  (projectId, quoteId, t) => async (dispatch) => {
    try {
      await NEW_API.post(`/api/projects-quotes-send/${projectId}/${quoteId}`, {
        data: {},
      });
      dispatch(fetchProject(projectId));
      toast.success(t("Quote successfully sent!"));
    } catch (error) {
      console.log(error);
      toastQuoteError(error, t);
    }
  };

/** Interlocutors are independent contact records (core /api/interlocutors). */
export const saveProjectInterlocutor =
  (interlocutor, projectId, t, callback) => async (dispatch) => {
    try {
      const { id, ...fields } = interlocutor;
      const body = { data: { ...fields, project: projectId } };
      if (id) {
        await API.put(`api/interlocutors/${id}`, body);
      } else {
        await API.post("api/interlocutors", body);
      }
      dispatch(fetchProject(projectId));
      toast.success(t("Interlocutor successfully saved!"));
      if (callback) callback();
    } catch (error) {
      console.log(error);
      toast.error(t("An error has occurred!"));
    }
  };

export const deleteProjectInterlocutor =
  (id, projectId, t) => async (dispatch) => {
    try {
      await API.delete(`api/interlocutors/${id}`);
      dispatch(fetchProject(projectId));
      toast.success(t("Interlocutor successfully deleted!"));
    } catch (error) {
      console.log(error);
      toast.error(t("An error has occurred!"));
    }
  };

/** Tunnel: create or update an action/reminder, then refresh the project. */
export const createUpdateProjectAction =
  (action, isReminder, status, projectId, t) => async (dispatch) => {
    try {
      const body = {
        data: {
          ...action,
          isReminder,
          status,
          date: action?.date ?? new Date(),
          type: action?.type?.code,
          project: projectId,
          interlocutor: undefined,
        },
      };
      if (action?.id) {
        await API.put(`api/actions/${action.id}`, body);
      } else {
        await API.post("api/actions", body);
      }
      // Status is stored on the project itself (like prospect.status).
      await NEW_API.put(`/api/projects-update/${projectId}`, {
        data: { status },
      });
      dispatch(fetchProject(projectId));
      dispatch({ type: "PROJECTS_TOUCHED" });
      toast.success(t("Action successfully saved!"));
    } catch (error) {
      console.log(error);
      toast.error(t("An error has occurred!"));
    }
  };

export const convertProjectReminderToAction =
  (id, projectId, t) => async (dispatch) => {
    try {
      await API.put(`api/actions/${id}`, { data: { isReminder: false } });
      dispatch(fetchProject(projectId));
      dispatch({ type: "PROJECTS_TOUCHED" });
    } catch (error) {
      console.log(error);
      toast.error(t("An error has occurred!"));
    }
  };

export const deleteProjectAction = (id, projectId, t) => async (dispatch) => {
  try {
    await API.delete(`api/actions/${id}`);
    dispatch(fetchProject(projectId));
    dispatch({ type: "PROJECTS_TOUCHED" });
    toast.success(t("Action successfully deleted!"));
  } catch (error) {
    console.log(error);
    toast.error(t("An error has occurred!"));
  }
};
